/**
 * Technician assignment eligibility.
 *
 * Two different per-user modality settings exist and must not be confused:
 *   - user_modalities.um_list     — csv, VISIBILITY only: which studies/orders
 *                                   the user is shown (modality-filter-helper).
 *   - user_modalities.um_modality — a single code, ASSIGNMENT only: which
 *                                   orders this technician can be assigned.
 *                                   NULL/'' = floater, assignable to anything.
 *
 * Mirrors the backend's isTechnicianAssignable() in
 * pacs-backend/src/controllers/modality-filter-helper.js — change both.
 */

/** Default hybrid-study owners, matching the backend's DOMINANT_MODALITIES. */
export const DEFAULT_DOMINANT_MODALITIES = ['PT'];

export const splitModalityCsv = (value?: string | null): string[] =>
  String(value || '')
    .split(',')
    .map(code => code.trim().toUpperCase())
    .filter(Boolean);

/**
 * Can a technician whose assignment modality is `userModality` take an order
 * whose po_modality is `orderModalityCsv`?
 *
 * po_modality is a csv for item-code-matched exams (T002): a PET-CT order is
 * stored as 'CT,PT'. Such hybrid studies are OWNED by their dominant modality
 * (PT by default) — the list filters hide a PET-CT from a plain CT filter, so
 * the dropdown must not offer a CT technician for it either. Only a PT
 * technician (or a floater) qualifies. 'CT,NM' has no dominant code under the
 * default config, so CT and NM technicians both qualify.
 */
export const isTechnicianAssignable = (
  userModality?: string | null,
  orderModalityCsv?: string | null,
  dominantModalities: string[] = DEFAULT_DOMINANT_MODALITIES
): boolean => {
  const mine = String(userModality || '').trim().toUpperCase();
  // No assignment modality configured => assignable to every order.
  if (!mine) {
    return true;
  }

  const orderCodes = splitModalityCsv(orderModalityCsv);
  // Modality not resolved yet (HIS order awaiting an item match) => never block.
  if (orderCodes.length === 0) {
    return true;
  }

  if (!orderCodes.includes(mine)) {
    return false;
  }

  const dominant = (dominantModalities?.length
    ? dominantModalities
    : DEFAULT_DOMINANT_MODALITIES
  ).map(code => String(code).trim().toUpperCase());

  return !orderCodes.some(code => code !== mine && dominant.includes(code));
};

/**
 * Which modality code(s) should body parts / report templates be looked up under
 * for an order whose po_modality is `orderModalityCsv`?
 *
 * Masters (body_parts_master.bp_modality, document_templates.dt_modality) always
 * hold a SINGLE code, but po_modality is a csv for item-code-matched exams (T002)
 * — a PET-CT order is stored as 'PT,CT'. Comparing the raw csv against a master
 * row can never match, which is why hybrid orders showed an empty body-part and
 * template dropdown in the report editor.
 *
 * Resolution follows the same ownership rule as the list filters and technician
 * assignment: a hybrid study belongs to its dominant modality (PT by default), so
 * a PET-CT reports under PT only — CT body parts/templates must NOT leak in.
 * A csv with no dominant code ('DX,CR', 'CT,NM') has no single owner, so every
 * code in it is a valid lookup key.
 */
export const resolveTemplateModalities = (
  orderModalityCsv?: string | null,
  dominantModalities: string[] = DEFAULT_DOMINANT_MODALITIES
): string[] => {
  const codes = splitModalityCsv(orderModalityCsv);
  if (codes.length <= 1) {
    return codes;
  }

  const dominant = (dominantModalities?.length
    ? dominantModalities
    : DEFAULT_DOMINANT_MODALITIES
  ).map(code => String(code).trim().toUpperCase());

  const owners = codes.filter(code => dominant.includes(code));
  return owners.length > 0 ? owners : codes;
};
